import React, { useState, useCallback, useMemo, useEffect } from "react";
import { StyleSheet, ScrollView } from "react-native";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "@lib/util/yup";
import { useForm } from "react-hook-form";
import { View, Card, Wrapper } from "@components/atoms/Themed";
import GradientButton from "@components/atoms/GradientButton";
import Spacer from "@components/atoms/Spacer";
import Divider from "@components/atoms/Divider";
import InputAccessoryView from "@components/atoms/InputAccessoryView";
import KeyboardAvoidingView from "@components/molecules/KeyboardAvoidingView";
import {
  TextField,
  Selectbox,
  ErrorText,
} from "@components/molecules/TextInput";
import ErrorDialog from "@components/molecules/dialog/ErrorDialog";
import NavigationBar from "@components/molecules/NavigationBar";
import { graphql, useFragment, useMutation } from "react-relay/hooks";
import {
  CampaignInputInvoiceInfluencer$key,
  InvoiceStatus,
} from "@generated/CampaignInputInvoiceInfluencer.graphql";
import { CampaignInputProfileMutation } from "@generated/CampaignInputProfileMutation.graphql";
import { KeyboardId, ProfileList } from "@constants/App";
import { notify } from "@lib/util/logger";

const profileQuery = graphql`
  fragment CampaignInputInvoiceInfluencer on Influencer {
    profile {
      invoiceStatus
      invoiceNo
    }
  }
`;

const profileMutation = graphql`
  mutation CampaignInputInvoiceMutation(
    $input: UpdateInfluencerInfoMutationInput!
  ) {
    updateInfluencerInfo(input: $input) {
      __typename
      ... on Influencer {
        profile {
          invoiceStatus
          invoiceNo
        }
      }
      ... on UpdateInfluencerInfoError {
        message
      }
    }
  }
`;

type Props = {
  profileFragment: CampaignInputInvoiceInfluencer$key;
  hasNext: boolean;
  onClose: () => void;
};

type ProfileInput = {
  invoiceStatus: InvoiceStatus;
  invoiceNo: string | null;
};

export default function CampaignInputInvoice({
  profileFragment,
  hasNext,
  onClose,
}: Props) {
  const { profile } = useFragment<CampaignInputInvoiceInfluencer$key>(
    profileQuery,
    profileFragment
  );
  const [status, setStatus] = useState<InvoiceStatus | null>(
    profile.invoiceStatus
  );
  const invoiceList = useMemo(
    () =>
      Object.keys(ProfileList.invoice_status).map((column) => ({
        label: ProfileList.invoice_status[column] as InvoiceStatus,
        value: column,
      })),
    []
  );

  const invoiceStatus = useMemo(
    () =>
      Object.keys(ProfileList.invoice_status).map(
        (key) => key as InvoiceStatus
      ),
    []
  );

  const [commit] = useMutation<CampaignInputProfileMutation>(profileMutation);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    resetField,
  } = useForm<ProfileInput>({
    defaultValues: {
      invoiceStatus: profile.invoiceStatus,
      invoiceNo: profile.invoiceNo,
    },
    mode: "all",
    resolver: yupResolver(
      object().shape({
        invoiceStatus: string()
          .required("選択してください")
          .oneOf(invoiceStatus, "選択してください"),
        invoiceNo: string()
          .default(null)
          .nullable()
          .test("checkInvoice", "13桁の半角数字を入力してください", (value) => {
            if (status === "applied") {
              return value !== null && /^[0-9]{13,13}$/.test(value);
            }
            return true;
          }),
      })
    ),
  });

  useEffect(() => {
    if (status !== "applied") {
      resetField("invoiceNo");
    }
  }, [status, resetField]);

  // コンポーネントマウント時のログ
  useEffect(() => {
    notify(
      {
        component: "CampaignInputInvoice",
        event: "mount",
        profileInvoiceStatus: profile.invoiceStatus,
        profileInvoiceNo: profile.invoiceNo,
        initialIsValid: isValid,
        validationErrors: errors,
        allowedInvoiceStatuses: invoiceStatus,
      },
      { level: "monitor" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // isValid状態の変化をログ
  useEffect(() => {
    notify(
      {
        component: "CampaignInputInvoice",
        event: "validationStateChange",
        isValid,
        errors: {
          invoiceStatus: errors.invoiceStatus?.message,
          invoiceNo: errors.invoiceNo?.message,
        },
        currentStatus: status,
      },
      { level: "monitor" }
    );
  }, [isValid, errors, status]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const save = useCallback(async () => {
    // 保存ボタン押下時のログ
    notify(
      {
        component: "CampaignInputInvoice",
        event: "saveButtonPressed",
        isValid,
        currentStatus: status,
      },
      { level: "monitor" }
    );

    await handleSubmit(
      async (submitInput: ProfileInput) => {
        // バリデーション成功時のログ
        notify(
          {
            component: "CampaignInputInvoice",
            event: "validationPassed",
            submitInput,
          },
          { level: "monitor" }
        );

        setLoading(true);
        await new Promise<void>((resolve) => {
          commit({
            variables: {
              input: {
                ...submitInput,
              },
            },
            onCompleted({ updateInfluencerInfo }) {
              // ミューテーション完了時のログ
              notify(
                {
                  component: "CampaignInputInvoice",
                  event: "mutationCompleted",
                  typename: updateInfluencerInfo.__typename,
                  result:
                    updateInfluencerInfo.__typename ===
                    "UpdateInfluencerInfoError"
                      ? { error: updateInfluencerInfo.message }
                      : { success: true },
                },
                { level: "monitor" }
              );

              if (
                updateInfluencerInfo.__typename === "UpdateInfluencerInfoError"
              ) {
                setError(updateInfluencerInfo.message);
              }
              setLoading(false);
              resolve();
            },
            onError(mutationError) {
              // ネットワークエラー等のログ
              notify(
                {
                  component: "CampaignInputInvoice",
                  event: "mutationError",
                  error: mutationError.message,
                },
                { level: "error" }
              );

              setError(mutationError.message);
              setLoading(false);
              resolve();
            },
          });
        });
      },
      (validationErrors) => {
        // バリデーション失敗時のログ
        notify(
          {
            component: "CampaignInputInvoice",
            event: "validationFailed",
            validationErrors,
          },
          { level: "error" }
        );

        // バリデーションエラーをユーザーに通知
        const errorMessages: string[] = [];
        if (validationErrors.invoiceStatus !== undefined) {
          errorMessages.push(validationErrors.invoiceStatus.message ?? "");
        }
        if (validationErrors.invoiceNo !== undefined) {
          errorMessages.push(validationErrors.invoiceNo.message ?? "");
        }
        if (errorMessages.length > 0) {
          setError(errorMessages.filter(Boolean).join("\n"));
        }
      }
    )();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit, handleSubmit]);

  return (
    <View style={styles.container}>
      <NavigationBar onClose={onClose} title="インボイス登録" />

      <Divider />

      <KeyboardAvoidingView>
        <ScrollView>
          <Card style={styles.content}>
            <Wrapper>
              <Selectbox
                control={control}
                emptyValue=""
                initialValue={profile.invoiceStatus}
                items={invoiceList}
                label="インボイス登録は申請していますか？"
                name="invoiceStatus"
                onChange={(value) => {
                  setStatus(value as InvoiceStatus | null);
                }}
              />
              {!isValid && errors.invoiceStatus !== undefined && (
                <ErrorText error={errors.invoiceStatus.message} />
              )}

              <Spacer height={16} />
              <TextField
                key={`toggle-${status}`}
                control={control}
                disabled={status !== "applied"}
                inputAccessoryViewID={KeyboardId}
                label="登録番号"
                maxLength={13}
                name="invoiceNo"
                placeholder="Tを除く13桁の半角数字を入力してください"
                type="numberString"
              />
              {!isValid && errors.invoiceNo !== undefined && (
                <ErrorText error={errors.invoiceNo.message} />
              )}
            </Wrapper>
          </Card>
          <Spacer height={100} />
        </ScrollView>
      </KeyboardAvoidingView>

      <Divider />
      <Card style={styles.footer}>
        <GradientButton
          disabled={!isValid}
          loading={loading}
          onPress={save}
          title={hasNext ? "次へ" : "情報を登録し、応募する"}
        />
      </Card>
      {error !== null && (
        <ErrorDialog message={error} onClose={() => setError(null)} />
      )}
      <InputAccessoryView nativeID={KeyboardId} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  footer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
  },
});
